import { useEffect, useMemo } from 'react';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import {
	DEFAULT_ENVIRONMENT_DISPLAY_NAME,
	DEFAULT_UNIT_ID_DISPLAY_NAME
} from 'src/common/consts/platformConfigConsts';
import { createAsyncLoadOptions, getInitialOptions } from 'src/common/helpers/AsyncFilterHelpers';

import { getWebSdkTestsFilterDefinitions } from '../../../helpers/WebSdkConsts';
import { useWebSdkTestLabelsQuery } from '../../../hooks/useWebSdkTestLabelsQuery';
import { getWebSdkTestsFilterDefinitionsV2 } from '../components/filter-section-v2/getWebSdkTestsFilterDefinitionsV2';
import { useWebSDKFiltersStore } from '../components/filter-section-v2/store';
import { LABEL_PREFIXES } from '../types/filter-section-v2';

const INITIAL_OPTIONS_COUNT = 200;

interface UseWebSdkTestsFiltersParams<T extends { labels: OptionType[] }> {
	allLabels?: OptionType[];
	selectedLabels?: OptionType[] | undefined;
	filterOptions: T;
	unitIdDisplayName?: string;
	environmentDisplayName?: string;
	isV2?: boolean;
}

interface UseWebSdkTestsFiltersReturn<T extends { labels: OptionType[] }> {
	filterDefinitions: IFilterDefinition[];
	mergedFilterOptions: T;
	isLoading: boolean;
}

export const useWebSdkTestsFilters = <T extends { labels: OptionType[] }>({
	allLabels: allLabelsProp,
	selectedLabels = [],
	filterOptions,
	unitIdDisplayName = DEFAULT_UNIT_ID_DISPLAY_NAME,
	environmentDisplayName = DEFAULT_ENVIRONMENT_DISPLAY_NAME,
	isV2 = false
}: UseWebSdkTestsFiltersParams<T>): UseWebSdkTestsFiltersReturn<T> => {
	const { data: queryLabels = [], isLoading: isQueryLoading } = useWebSdkTestLabelsQuery();

	const storeAllLabels = useWebSDKFiltersStore((state) => state.allLabels);
	const storeIsLoading = useWebSDKFiltersStore((state) => state.isLoading);

	const { loadLabels } = useWebSDKFiltersStore();
	useEffect(() => {
		if (isV2) {
			loadLabels();
		}
	}, [isV2, loadLabels]);

	const allLabels = useMemo(() => {
		if (isV2) {
			return storeAllLabels.length > 0 ? storeAllLabels : (allLabelsProp ?? []);
		}
		return allLabelsProp ?? queryLabels;
	}, [isV2, storeAllLabels, allLabelsProp, queryLabels]);

	const isLoading = isV2 ? storeIsLoading : isQueryLoading;

	const customLabels = useMemo(() => {
		if (!isV2) {
			return allLabels;
		}

		return allLabels.filter((label) => {
			const labelValue = String(label.value);
			if (labelValue.startsWith(LABEL_PREFIXES.ENVIRONMENT)) {
				return false;
			}
			if (labelValue.toLowerCase().startsWith(LABEL_PREFIXES.UNIT_ID)) {
				return false;
			}
			return true;
		});
	}, [isV2, allLabels]);

	const initialOptions = useMemo(
		() => getInitialOptions(customLabels, selectedLabels, INITIAL_OPTIONS_COUNT),
		[customLabels, selectedLabels]
	);

	const loadOptions = useMemo(
		() => createAsyncLoadOptions(customLabels, initialOptions),
		[customLabels, initialOptions]
	);

	const filterDefinitions = useMemo(() => {
		if (isV2) {
			return getWebSdkTestsFilterDefinitionsV2(
				unitIdDisplayName,
				environmentDisplayName,
				loadOptions,
				initialOptions
			);
		}
		return getWebSdkTestsFilterDefinitions(loadOptions, initialOptions);
	}, [isV2, unitIdDisplayName, environmentDisplayName, loadOptions, initialOptions]);

	const mergedFilterOptions = useMemo(
		() => ({
			...filterOptions,
			labels: initialOptions
		}),
		[filterOptions, initialOptions]
	);

	return {
		filterDefinitions,
		mergedFilterOptions,
		isLoading
	};
};
